import { format } from 'date-fns';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    humanizeBytes,
    inject,
    Input,
    Modal,
    ModalHandle,
    NothingHere,
    TasksStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArchiveFill } from '@react-icons/all-files/bs/BsArchiveFill';
import { Form, FormGroup } from 'reactstrap';
import { FileSystemsStore, IImportModel } from './FileSystemsStore';

export interface IFileSystemAttachmentsModalProps {
    readonly fileSystem: Mri.FileSystems.FileSystem;
    readonly modalHandle: ModalHandle;
}

@autoinject
@observer
export class FileSystemAttachmentsModal extends React.Component<IFileSystemAttachmentsModalProps> {
    public render() {
        const { fileSystem } = this.props;
        return (
            <Modal title={`${fileSystem.fileName} Attachments`} modalHandle={this.props.modalHandle} className="p-0">
                <p>
                    <b>NOTE: FileSystem attachments are NOT dependencies.</b>
                </p>
                <p>FileSystem attachments are not meant to be directly uploaded to an MRI display.</p>
                <p>Please avoid uploading tile driver hex files to a MRI FileSystem</p>

                <Form encType="multipart/form-data" onSubmit={this.uploadAsync} className="form-inline">
                    <FormGroup className="d-inline-block">
                        <input
                            type="submit"
                            value={this.uploading ? 'Uploading...' : 'Upload'}
                            className="btn btn-primary me-2"
                            disabled={this.uploading || !this.importForm.fields.files.value?.length}
                        />
                    </FormGroup>

                    <FormGroup className="d-inline-block">
                        <Input
                            type="file"
                            id="file-system-attachment-upload"
                            field={this.importForm.fields.files}
                            multiple
                        />
                    </FormGroup>
                </Form>

                <table className="table table-striped fixed-header">
                    <thead className="thead">
                        <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Updated</th>
                            <th scope="col">Size</th>
                        </tr>
                    </thead>

                    <tbody>
                        {this.props.fileSystem?.attachments?.map(a => (
                            <tr key={a.fileName}>
                                <td>
                                    <a href={a.url} className="btn btn-link p-0" style={{ fontWeight: 'normal' }}>
                                        {a.fileName}
                                    </a>
                                </td>
                                <td>{a.updated ? format(a.updated, 'MM/d/yyyy') : '-'}</td>
                                <td>{humanizeBytes(a.fileSize)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <NothingHere
                    empty={!this.props.fileSystem.attachments?.length}
                    icon={<BsArchiveFill />}
                    loading={false}
                    loadingMessage="Loading..."
                />
            </Modal>
        );
    }

    @bind
    @action
    private async uploadAsync(event: React.FormEvent<HTMLFormElement>) {
        this.uploading = true;
        event.preventDefault();
        const task = this.tasksStore.startSessionTask(`Upload file(s)`);

        try {
            await this.fileSystemsStore.uploadAttachment(this.props.fileSystem.fileSystemGuid, this.importForm.values);
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.uploading = false;
        }
    }

    @observable private uploading = false;
    @observable
    private readonly importForm = new FormStore<IImportModel>({
        files: null,
    });
    @inject
    private fileSystemsStore!: FileSystemsStore;
    @inject
    private tasksStore!: TasksStore;
}
